import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Card, ProgressBar } from "react-bootstrap";
import { reportMeteredUsage } from "../../../Services/StripeService";
import { getAllSupportTeamMembers } from "../../../Services/LeaderSupportTeamMember";
import SpinnerLoader from "../../../Components/SpinnerLoader";

// Helper function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function SubscriptionProcessing() {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [priceId, setPriceId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [teamMemberCount, setTeamMemberCount] = useState(0);
  const history = useHistory();
  const query = useQuery();

  // Get customerId and other data from query params
  useEffect(() => {
    const cid = query.get("customer_id");
    const pid = query.get("price_id");

    if (!cid || !pid) {
      setStatus("error");
      setMessage(
        "Missing customer or price information. Please contact support."
      );
      return;
    }

    setCustomerId(cid);
    setPriceId(pid);
    processSubscription(cid, pid);
  }, [query]);

  const processSubscription = async (customerId, priceId) => {
    try {
      // Step 1: Get team member count
      setCurrentStep(1);
      const res = await getAllSupportTeamMembers();
      const result = JSON.parse(res.response);

      if (!result || result.activeSupportTeamCount === undefined) {
        throw new Error("Failed to get support team member count");
      }

      const count = result.activeSupportTeamCount;
      setTeamMemberCount(count);
      setCurrentStep(2);

      // Step 2: Report usage to Stripe
      const reportResult = await reportMeteredUsage(customerId, count, priceId);

      if (!reportResult.success) {
        throw new Error(
          reportResult.error || "Failed to report usage to Stripe"
        );
      }

      setCurrentStep(3);
      setStatus("success");

      // Automatically redirect after 3 seconds
      setTimeout(() => {
        history.push("/groupleader/groupleaderdashboard");
      }, 3000);
    } catch (err) {
      console.error("Error in subscription processing:", err);
      setStatus("error");
      setMessage(
        err.message || "An unexpected error occurred during subscription setup."
      );
    }
  };

  return (
    <div className="container p-0">
      <h1 className="page-title">Finalizing Your Subscription</h1>

      <div className="dashboard-wrapper">
        <Card className="mx-auto" style={{ maxWidth: "700px" }}>
          <Card.Body className="p-4">
            <h3 className="text-center mb-4">Setting Up Your Account</h3>

            <ProgressBar
              now={currentStep * 33.3}
              label={`${Math.round(currentStep * 33.3)}%`}
              className="mb-4"
              variant={status === "error" ? "danger" : "warning"}
            />

            <div className="steps-container">
              <div
                className={`step ${currentStep >= 1 ? "active" : ""} ${
                  status === "error" && currentStep === 1 ? "error" : ""
                }`}
              >
                <div className="step-number">1</div>
                <div className="step-content">
                  <h5>Retrieving Support Team Information</h5>
                  {currentStep === 1 && status !== "error" && <SpinnerLoader />}
                  {currentStep > 1 && (
                    <div className="text-success">
                      ✓ Found {teamMemberCount} support team members
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`step ${currentStep >= 2 ? "active" : ""} ${
                  status === "error" && currentStep === 2 ? "error" : ""
                }`}
              >
                <div className="step-number">2</div>
                <div className="step-content">
                  <h5>Configuring Metered Billing</h5>
                  {currentStep === 2 && status !== "error" && <SpinnerLoader />}
                  {currentStep > 2 && (
                    <div className="text-success">
                      ✓ Metered billing configured
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`step ${currentStep >= 3 ? "active" : ""} ${
                  status === "error" && currentStep === 3 ? "error" : ""
                }`}
              >
                <div className="step-number">3</div>
                <div className="step-content">
                  <h5>Finalizing Subscription</h5>
                  {currentStep === 3 && status !== "error" && <SpinnerLoader />}
                  {currentStep >= 3 && status === "success" && (
                    <div className="text-success">✓ Subscription activated</div>
                  )}
                </div>
              </div>
            </div>

            {status === "error" && (
              <Alert variant="danger" className="mt-4">
                <h5>Subscription Setup Error</h5>
                <p>{message}</p>
                <p>Please contact support for assistance or try again later.</p>
              </Alert>
            )}

            {status === "success" && (
              <Alert variant="success" className="mt-4">
                <h5>Subscription Successfully Activated!</h5>
                <p>
                  Your subscription has been set up and your account is ready to
                  use.
                </p>
                <p>Redirecting to dashboard...</p>
              </Alert>
            )}

            <div className="text-center mt-4">
              <Button
                variant="primary"
                onClick={() =>
                  history.push("/groupleader/groupleaderdashboard")
                }
              >
                Go to Dashboard
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>

      <style jsx>{`
        .steps-container {
          margin: 2rem 0;
        }
        .step {
          display: flex;
          margin-bottom: 1.5rem;
          opacity: 0.5;
        }
        .step.active {
          opacity: 1;
        }
        .step.error .step-number {
          background-color: #dc3545;
        }
        .step-number {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #ffc107;
          color: #fff;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
        }
        .step-content {
          flex: 1;
        }
      `}</style>
    </div>
  );
}
